<template>
  <div class="dashboard">

    <!-- 🔥 HEADLINE -->
    <div class="headline">
      <h1>{{ $t('dashboard.title') }}</h1>
      <p class="insight">
        Backend data only. Last synced from API:
        <span class="trend positive">{{ lastSyncedLabel }}</span>
      </p>
    </div>

    <div v-if="loading" class="dashboard-loader" aria-live="polite" aria-busy="true">
      <div class="loader-top">
        <div>
          <span class="loader-pill"></span>
          <h2>Preparing your dashboard</h2>
          <p>Syncing agents, conversations, contacts, and bookings from the API.</p>
        </div>
        <div class="loader-spinner" aria-hidden="true"></div>
      </div>

      <div class="skeleton-stats">
        <div v-for="item in 4" :key="`stat-${item}`" class="skeleton-card">
          <span class="skeleton-line short"></span>
          <span class="skeleton-number"></span>
          <span class="skeleton-line"></span>
        </div>
      </div>

      <div class="skeleton-charts">
        <div class="skeleton-chart-card">
          <span class="skeleton-line title"></span>
          <div class="skeleton-chart line-chart">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div class="skeleton-chart-card">
          <span class="skeleton-line title"></span>
          <div class="skeleton-chart bar-chart">
            <span style="height: 42%"></span>
            <span style="height: 68%"></span>
            <span style="height: 54%"></span>
            <span style="height: 78%"></span>
            <span style="height: 62%"></span>
          </div>
        </div>
      </div>

      <div class="skeleton-actions">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>

    <div v-else>

      <!-- KPI -->
      <div class="stats-grid">

        <div class="stat-card">
          <h3>{{ $t('dashboard.agents') }}</h3>
          <p class="stat-value">{{ stats.agents }}</p>
          <span class="stat-sub">{{ stats.sessions }} backend sessions</span>
        </div>

        <div class="stat-card">
          <h3>{{ $t('dashboard.messages') }}</h3>
          <p class="stat-value">{{ stats.messages }}</p>
          <span class="stat-sub">{{ avgTokens }} avg tokens</span>
        </div>

        <div class="stat-card">
          <h3>Contacts</h3>
          <p class="stat-value">{{ stats.contacts }}</p>
          <span class="stat-sub">{{ conversionRate }}% conversion</span>
        </div>

        <div class="stat-card highlight">
          <h3>Calls remaining</h3>
          <p class="stat-value">{{ callsRemaining }}</p>
          <span class="stat-sub light">{{ planLabel }}</span>
        </div>

      </div>

      <!-- ALERT -->
      <div v-if="stats.sessions > 0 && stats.contacts === 0" class="alert-card">
        No contacts are visible in backend session data yet. Check that the agent collects name or phone.
      </div>

      <!-- CHARTS -->
      <div class="charts">

        <div class="chart-card">
          <h3>Contacts per day</h3>
          <Line v-if="hasContactsChart" :data="contactsChartData" />
          <p v-else class="empty-chart">No backend contact data yet.</p>
        </div>

        <div class="chart-card">
          <h3>Sessions per day</h3>
          <Bar v-if="hasSessionsChart" :data="sessionsChartData" />
          <p v-else class="empty-chart">No backend session data yet.</p>
        </div>

      </div>

      <!-- INSIGHTS -->
      <div class="insights">

        <div class="insight-card">
          <strong>{{ $t('dashboard.conversion') }}</strong>
          <p>{{ conversionRate }}%</p>
        </div>

        <div class="insight-card">
          <strong>{{ $t('dashboard.todayActivity') }}</strong>
          <p>{{ todaySessions }} sessions</p>
        </div>

        <div class="insight-card">
          <strong>Backend source</strong>
          <p>{{ performanceLabel }}</p>
        </div>

      </div>

      <!-- ACTIONS -->
      <div class="actions">
        <button @click="$router.push('/create-agent')">+ Create Agent</button>
        <button @click="$router.push('/app/test-live')">Test Call Now</button>
        <button @click="$router.push('/app/live-monitor')">View Transcripts</button>
      </div>

      <div class="operations-grid">
        <section class="panel-card">
          <div class="panel-heading">
            <div>
              <h2>Agents</h2>
              <p>Your AI receptionists and readiness status.</p>
            </div>
            <button class="small-button" @click="$router.push('/agents')">Manage</button>
          </div>

          <div v-if="agentsList.length === 0" class="empty-panel">
            No agents yet. Create one to start testing calls.
          </div>

          <div v-else class="agent-list">
            <button
              v-for="item in agentsList"
              :key="item.id"
              type="button"
              class="agent-row"
              :class="{ active: String(item.id) === String(activeAgentId) }"
              @click="setActiveAgent(item.id)"
            >
              <div>
                <strong>{{ item.name || item.business_name || `Agent #${item.id}` }}</strong>
                <span>{{ item.industry_key || 'general' }} · {{ item.booking_enabled ? 'booking on' : 'booking off' }}</span>
              </div>
              <span class="status-pill">{{ item.active === 0 ? 'Paused' : 'Active' }}</span>
            </button>
          </div>
        </section>

        <section class="panel-card calls-panel">
          <div class="panel-heading">
            <div>
              <h2>Calls history</h2>
              <p>Latest sessions with transcript, summary, outcome, and lead status.</p>
            </div>
            <button class="small-button" @click="$router.push('/app/live-monitor')">Open monitor</button>
          </div>

          <div v-if="recentCalls.length === 0" class="empty-panel">
            No calls yet. Use Test Call Now to generate your first transcript.
          </div>

          <div v-else class="calls-layout">
            <div class="call-list">
              <button
                v-for="call in recentCalls"
                :key="call.id"
                type="button"
                class="call-row"
                :class="{ selected: selectedCallId === call.id }"
                @click="selectedCallId = call.id"
              >
                <div>
                  <strong>{{ call.name || call.phone || `Call #${call.id}` }}</strong>
                  <span>{{ formatSessionDate(call) }}</span>
                </div>
                <span class="status-pill">{{ getLeadStatus(call) }}</span>
              </button>
            </div>

            <div class="call-detail">
              <div class="summary-card">
                <span>AI summary</span>
                <p>{{ selectedCallSummary }}</p>
              </div>

              <div class="detail-grid">
                <div>
                  <span>Outcome</span>
                  <strong>{{ selectedCallOutcome }}</strong>
                </div>
                <div>
                  <span>Lead status</span>
                  <strong>{{ selectedCallLeadStatus }}</strong>
                </div>
                <div>
                  <span>Next action</span>
                  <strong>{{ selectedCallNextAction }}</strong>
                </div>
                <div>
                  <span>Transcript</span>
                  <strong>{{ selectedCallTranscriptStatus }}</strong>
                </div>
              </div>

              <button class="small-button dark" @click="$router.push('/app/live-monitor')">
                View transcript
              </button>
            </div>
          </div>
        </section>
      </div>

    </div>

    <p v-if="error" class="error">{{ error }}</p>

  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { Line, Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement
} from 'chart.js'

import * as api from '@/services/api.js'
import Cookies from 'js-cookie'
import { useRouter } from 'vue-router'

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement
)

const router = useRouter()

const stats = ref({
  agents: 0,
  messages: 0,
  contacts: 0,
  bookings: 0,
  sessions: 0,
})

const trialCallLimit = 10
const agentsList = ref([])
const sessionList = ref([])
const activeAgentId = ref(null)
const selectedCallId = ref(null)
const avgTokens = ref(0)
const loading = ref(true)
const error = ref('')

const contactsChartData = ref({ labels: [], datasets: [] })
const sessionsChartData = ref({ labels: [], datasets: [] })

const conversionRate = ref(0)
const todaySessions = ref(0)
const performanceLabel = ref('')
const lastSyncedAt = ref(null)
const planLabel = ref('10 free calls')

const callsRemaining = computed(() => {
  return Math.max(trialCallLimit - stats.value.sessions, 0)
})

const recentCalls = computed(() => {
  return [...sessionList.value]
    .sort((a, b) => new Date(b.started_at || b.created_at || 0) - new Date(a.started_at || a.created_at || 0))
    .slice(0, 8)
})

const selectedCall = computed(() => {
  return recentCalls.value.find(call => call.id === selectedCallId.value) || recentCalls.value[0] || null
})

const selectedCallSummary = computed(() => {
  const call = selectedCall.value
  if (!call) return 'No call selected yet.'

  if (call.ai_summary) return call.ai_summary
  if (call.summary) return call.summary

  const details = [
    call.name ? `Caller: ${call.name}` : '',
    call.phone ? `Phone: ${call.phone}` : '',
    call.service ? `Need: ${call.service}` : '',
    call.scheduled_at ? `Appointment: ${formatDateTime(call.scheduled_at)}` : ''
  ].filter(Boolean)

  return details.length
    ? details.join('. ')
    : 'Transcript summary will appear after the AI call has enough captured details.'
})

const selectedCallOutcome = computed(() => selectedCall.value ? getCallOutcome(selectedCall.value) : 'No call')
const selectedCallLeadStatus = computed(() => selectedCall.value ? getLeadStatus(selectedCall.value) : 'No lead')
const selectedCallNextAction = computed(() => selectedCall.value ? getNextAction(selectedCall.value) : 'Test call now')
const selectedCallTranscriptStatus = computed(() => {
  const call = selectedCall.value
  if (!call) return 'Unavailable'
  return Number(call.message_count || call.messages || 0) > 0 ? `${call.message_count || call.messages} messages` : 'Pending'
})

const hasContactsChart = computed(() => {
  return contactsChartData.value?.datasets?.[0]?.data?.some(value => Number(value) > 0)
})

const hasSessionsChart = computed(() => {
  return sessionsChartData.value?.datasets?.[0]?.data?.some(value => Number(value) > 0)
})

const lastSyncedLabel = computed(() => {
  return lastSyncedAt.value
    ? lastSyncedAt.value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : 'not synced'
})

function normalizeRows(response) {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.sessions)) return response.sessions
  return []
}

function isContactSession(session) {
  return Boolean(
    session?.name ||
    session?.phone ||
    session?.email ||
    session?.service
  )
}

function isBookedSession(session) {
  return Boolean(Number(session?.booking_completed ?? 0)) ||
    session?.step === 'booking_completed' ||
    Boolean(session?.scheduled_at)
}

function getLeadStatus(session) {
  if (!session) return 'No lead'
  if (isBookedSession(session)) return 'Booked'
  if (isContactSession(session)) return 'Captured'
  if (session.ended_at) return 'Needs review'
  return 'In progress'
}

function getCallOutcome(session) {
  if (isBookedSession(session)) return 'Appointment booked'
  if (session?.step === 'deposit_link_sent' || session?.step === 'deposit_pending') return 'Deposit requested'
  if (isContactSession(session)) return 'Lead captured'
  if (session?.ended_at) return 'Completed without lead'
  return 'Call in progress'
}

function getNextAction(session) {
  if (isBookedSession(session)) return 'Confirm appointment'
  if (session?.step === 'deposit_link_sent' || session?.step === 'deposit_pending') return 'Watch payment status'
  if (isContactSession(session)) return 'Call back or qualify'
  if (session?.ended_at) return 'Review transcript'
  return 'Wait for caller response'
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function formatSessionDate(session) {
  return formatDateTime(session?.started_at || session?.created_at) || 'No date'
}

function setActiveAgent(agentId) {
  Cookies.set('active_agent_id', agentId)
  activeAgentId.value = agentId
}

function getSessionDateLabel(session) {
  const rawDate = session?.started_at || session?.created_at || session?.scheduled_at
  if (!rawDate) return null

  const date = new Date(rawDate)
  if (Number.isNaN(date.getTime())) return null

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric'
  })
}

function buildDailyStats(sessions) {
  const buckets = new Map()

  for (const session of sessions) {
    const label = getSessionDateLabel(session)
    if (!label) continue

    const current = buckets.get(label) || {
      date: label,
      sessions: 0,
      contacts: 0
    }

    current.sessions += 1
    if (isContactSession(session)) current.contacts += 1
    buckets.set(label, current)
  }

  return Array.from(buckets.values()).slice(-7)
}

onMounted(async () => {
  try {
    const token = Cookies.get('token')
    if (!token) return router.push('/sign-in')

    let agentId = Cookies.get('active_agent_id')
    activeAgentId.value = agentId

    const userStats = await api.analytics.forUsers()
    const agents = await api.agents.getAll()
    const agentRows = normalizeRows(agents)
    agentsList.value = agentRows

    if (!agentId && agentRows.length) {
      agentId = agentRows[0].id
      Cookies.set('active_agent_id', agentId)
      activeAgentId.value = agentId
    }

    if (!agentId) {
      stats.value = {
        agents: userStats?.totalAgents || agentRows.length || 0,
        messages: 0,
        contacts: 0,
        bookings: 0,
        sessions: 0
      }
      performanceLabel.value = 'Create an agent to start collecting backend activity.'
      lastSyncedAt.value = new Date()
      return
    }

    const agentStats = await api.analytics.forAgent(agentId)
    const sessionRows = normalizeRows(await api.sessions.getAll(agentId))
    sessionList.value = sessionRows
    selectedCallId.value = sessionRows[0]?.id || null
    const dailyStats = buildDailyStats(sessionRows)
    const contactsCount = sessionRows.filter(isContactSession).length
    const bookingsCount = sessionRows.filter(isBookedSession).length

    stats.value = {
      agents: userStats?.totalAgents || agentRows.length || 0,
      messages: agentStats?.totalMessages || 0,
      contacts: contactsCount,
      bookings: bookingsCount,
      sessions: agentStats?.totalSessions || sessionRows.length || 0
    }

    avgTokens.value = agentStats?.averageTokensUsed || 0

    contactsChartData.value = {
      labels: dailyStats.map(d => d.date),
      datasets: [
        {
          label: 'Contacts',
          data: dailyStats.map(d => d.contacts),
          borderColor: '#0d8f62',
          backgroundColor: 'rgba(13,143,98,0.15)',
          fill: true,
          tension: 0.4,
          pointRadius: 3
        }
      ]
    }

    sessionsChartData.value = {
      labels: dailyStats.map(d => d.date),
      datasets: [
        {
          label: 'Sessions',
          data: dailyStats.map(d => d.sessions),
          backgroundColor: '#17211b'
        }
      ]
    }

    conversionRate.value = stats.value.sessions
      ? Math.round((stats.value.contacts / stats.value.sessions) * 100)
      : 0

    const todayLabel = new Date().toLocaleDateString([], {
      month: 'short',
      day: 'numeric'
    })
    todaySessions.value = dailyStats.find(d => d.date === todayLabel)?.sessions || 0

    performanceLabel.value =
      stats.value.sessions === 0 ? 'No backend sessions yet' :
      conversionRate.value > 20 ? 'Strong contact capture' :
      conversionRate.value > 10 ? 'Contact capture active' :
      'Review collection prompts'

    lastSyncedAt.value = new Date()

  } catch (err) {
    error.value = 'Erreur dashboard'
    console.error(err)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.dashboard {
  padding: 2rem;
}

/* HEADLINE */
.headline {
  margin-bottom: 20px;
}

.insight {
  color: #6b7280;
}

.trend {
  margin-left: 10px;
  font-weight: 600;
}

.trend.positive {
  color: #22c55e;
}

.trend.negative {
  color: #ef4444;
}

/* KPI */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
}

.stat-card.highlight {
  background: #6366f1;
  color: white;
}

.stat-card.danger {
  background: #ef4444;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
}

.stat-sub {
  font-size: 0.8rem;
  color: #6b7280;
}

.stat-sub.light {
  color: rgba(255, 255, 255, 0.75);
}

.warning {
  font-size: 0.8rem;
}

/* ALERT */
.alert-card {
  margin-top: 1rem;
  padding: 15px;
  background: #fef3c7;
  border-radius: 10px;
}

/* CHARTS */
.charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 2rem;
}

.chart-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
}

.empty-chart {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: #6b7280;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  background: #f9fafb;
}

/* INSIGHTS */
.insights {
  display: flex;
  gap: 20px;
  margin-top: 2rem;
}

.insight-card {
  background: #111827;
  color: white;
  padding: 20px;
  border-radius: 12px;
}

/* ACTIONS */
.actions {
  margin-top: 2rem;
  display: flex;
  gap: 15px;
}

.actions button {
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: #6366f1;
  color: white;
  cursor: pointer;
}

.actions button:hover {
  background: #4f46e5;
}

.operations-grid {
  display: grid;
  grid-template-columns: minmax(260px, 0.85fr) minmax(0, 1.45fr);
  gap: 1.5rem;
  margin-top: 2rem;
}

.panel-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 16px 42px rgba(15, 23, 42, 0.05);
}

.panel-heading {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.panel-heading h2 {
  margin: 0;
  color: #111827;
  font-size: 1.15rem;
}

.panel-heading p {
  margin: 0.35rem 0 0;
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.5;
}

.small-button {
  flex: 0 0 auto;
  border: 0;
  border-radius: 10px;
  background: #e8f5ef;
  color: #0d8f62;
  cursor: pointer;
  font-weight: 800;
  padding: 0.65rem 0.85rem;
}

.small-button.dark {
  margin-top: 1rem;
  background: #111827;
  color: #ffffff;
}

.empty-panel {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  color: #64748b;
  padding: 1rem;
  line-height: 1.6;
}

.agent-list,
.call-list {
  display: grid;
  gap: 0.75rem;
}

.agent-row,
.call-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  padding: 0.9rem;
  text-align: left;
}

.agent-row.active,
.call-row.selected {
  border-color: #0d8f62;
  background: #f0fdf4;
}

.agent-row strong,
.call-row strong {
  display: block;
  color: #0f172a;
}

.agent-row span,
.call-row span {
  color: #64748b;
  font-size: 0.82rem;
}

.status-pill {
  white-space: nowrap;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca !important;
  font-weight: 800;
  padding: 0.35rem 0.6rem;
}

.calls-layout {
  display: grid;
  grid-template-columns: minmax(220px, 0.8fr) minmax(0, 1.2fr);
  gap: 1rem;
}

.call-detail {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
  padding: 1rem;
}

.summary-card {
  border-radius: 12px;
  background: #111827;
  color: #ffffff;
  padding: 1rem;
}

.summary-card span,
.detail-grid span {
  display: block;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.summary-card p {
  margin: 0.45rem 0 0;
  color: #e5e7eb;
  line-height: 1.6;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.85rem;
}

.detail-grid div {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  padding: 0.8rem;
}

.detail-grid strong {
  display: block;
  margin-top: 0.35rem;
  color: #0f172a;
  line-height: 1.35;
}

.dashboard-loader {
  display: grid;
  gap: 1.5rem;
}

.loader-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
  background:
    radial-gradient(circle at top left, rgba(13, 143, 98, 0.14), transparent 32%),
    #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.06);
}

.loader-top h2 {
  margin: 0.4rem 0 0.35rem;
  color: #111827;
  font-size: 1.2rem;
}

.loader-top p {
  margin: 0;
  color: #6b7280;
}

.loader-pill {
  display: block;
  width: 150px;
  height: 9px;
  border-radius: 999px;
  background: linear-gradient(90deg, #0d8f62, #6366f1);
}

.loader-spinner {
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  border-radius: 999px;
  border: 4px solid #e5e7eb;
  border-top-color: #0d8f62;
  animation: spin 0.9s linear infinite;
}

.skeleton-stats,
.skeleton-charts {
  display: grid;
  gap: 1.5rem;
}

.skeleton-stats {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.skeleton-charts {
  grid-template-columns: 1fr 1fr;
}

.skeleton-card,
.skeleton-chart-card {
  background: white;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  box-shadow: 0 16px 42px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  position: relative;
}

.skeleton-card {
  min-height: 145px;
  padding: 1.5rem;
}

.skeleton-chart-card {
  min-height: 270px;
  padding: 20px;
}

.skeleton-card::after,
.skeleton-chart-card::after,
.skeleton-actions span::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent);
  animation: shimmer 1.35s infinite;
}

.skeleton-line,
.skeleton-number {
  display: block;
  border-radius: 999px;
  background: #e8edf3;
}

.skeleton-line {
  width: 78%;
  height: 12px;
  margin-top: 18px;
}

.skeleton-line.short {
  width: 42%;
  margin-top: 0;
}

.skeleton-line.title {
  width: 34%;
  margin-top: 0;
  margin-bottom: 2rem;
}

.skeleton-number {
  width: 88px;
  height: 38px;
  margin: 1.4rem auto 0;
}

.skeleton-chart {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  height: 185px;
  padding: 1rem;
  border-radius: 10px;
  background: #f9fafb;
}

.skeleton-chart span {
  display: block;
  flex: 1;
  border-radius: 999px 999px 4px 4px;
  background: #e2e8f0;
}

.line-chart {
  align-items: center;
}

.line-chart span {
  height: 8px;
  border-radius: 999px;
}

.line-chart span:nth-child(1) { transform: translateY(34px); }
.line-chart span:nth-child(2) { transform: translateY(8px); }
.line-chart span:nth-child(3) { transform: translateY(22px); }
.line-chart span:nth-child(4) { transform: translateY(-18px); }
.line-chart span:nth-child(5) { transform: translateY(-4px); }

.skeleton-actions {
  display: flex;
  gap: 15px;
}

.skeleton-actions span {
  position: relative;
  width: 148px;
  height: 44px;
  overflow: hidden;
  border-radius: 8px;
  background: #e8edf3;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error {
  color: red;
}

@media (max-width: 900px) {
  .charts {
    grid-template-columns: 1fr;
  }

  .skeleton-charts {
    grid-template-columns: 1fr;
  }

  .insights,
  .actions,
  .skeleton-actions {
    flex-direction: column;
  }

  .loader-top {
    align-items: flex-start;
  }

  .operations-grid,
  .calls-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .dashboard {
    padding: 1rem;
  }

  .loader-top {
    padding: 1rem;
  }

  .loader-spinner {
    width: 34px;
    height: 34px;
  }

  .skeleton-card {
    min-height: 124px;
  }

  .skeleton-chart-card {
    min-height: 230px;
  }

  .skeleton-chart {
    height: 145px;
  }

  .skeleton-actions span {
    width: 100%;
  }
}
</style>
